import { t as useTrackEvent } from "./useTrackEvent-IE9Pu1uh.js";
import { useCallback, useMemo, useRef } from "react";
import { useStatsigClient } from "@statsig/react-bindings";
import { forceStartRecording, runStatsigTriggeredSessionReplay, stopRecording } from "@statsig/session-replay";

//#region src/hooks/useSessionReplayCapture.ts
/**
* Imperative API for Statsig triggered session replay.
*
* Requires the provider to have session replay enabled (default).
* Maintains single-session semantics: a second start for a different sessionKey
* is blocked and tracked instead of starting a second recording.
*/
function useSessionReplayCapture() {
	const { client } = useStatsigClient();
	const { trackEvent } = useTrackEvent();
	const sessionStateRef = useRef({
		isRecording: false,
		sessionKey: null,
		startTime: null
	});
	const startCapture = useCallback((sessionKey, metadata) => {
		const state = sessionStateRef.current;
		if (state.isRecording && state.sessionKey !== sessionKey) {
			trackEvent("session_replay_requested", { metadata: {
				sessionKey,
				blockedBy: state.sessionKey || "",
				...metadata
			} });
			return false;
		}
		if (state.isRecording && state.sessionKey === sessionKey) return true;
		sessionStateRef.current = {
			isRecording: true,
			sessionKey,
			startTime: Date.now()
		};
		forceStartRecording();
		trackEvent("session_replay_started", { metadata: {
			sessionKey,
			...metadata
		} });
		return true;
	}, [trackEvent]);
	const stopCapture = useCallback((sessionKey, metadata) => {
		const state = sessionStateRef.current;
		trackEvent("session_replay_stop_requested", { metadata: {
			sessionKey,
			currentSession: state.sessionKey || "",
			isRecording: String(state.isRecording),
			...metadata
		} });
		if (!state.isRecording || state.sessionKey !== sessionKey) return false;
		const duration = state.startTime ? Date.now() - state.startTime : 0;
		trackEvent("session_replay_stopped", {
			value: duration,
			metadata: {
				sessionKey,
				duration: String(duration),
				...metadata
			}
		});
		stopRecording();
		runStatsigTriggeredSessionReplay(client);
		sessionStateRef.current = {
			isRecording: false,
			sessionKey: null,
			startTime: null
		};
		return true;
	}, [trackEvent, client]);
	const checkIsRecording = useCallback((sessionKey) => {
		const state = sessionStateRef.current;
		if (sessionKey) return state.isRecording && state.sessionKey === sessionKey;
		return state.isRecording;
	}, []);
	const getCurrentSessionKey = useCallback(() => {
		return sessionStateRef.current.sessionKey;
	}, []);
	const getSessionDuration = useCallback(() => {
		const state = sessionStateRef.current;
		if (!state.isRecording || !state.startTime) return 0;
		return Date.now() - state.startTime;
	}, []);
	return useMemo(() => ({
		startCapture,
		stopCapture,
		checkIsRecording,
		getCurrentSessionKey,
		getSessionDuration
	}), [
		startCapture,
		stopCapture,
		checkIsRecording,
		getCurrentSessionKey,
		getSessionDuration
	]);
}

//#endregion
export { useSessionReplayCapture };
//# sourceMappingURL=session-replay.js.map